import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Calendar, Globe, ShieldCheck, FileText } from "lucide-react";

const COPY = {
  en: {
    eyebrow: "Pre-Orders Open · Allocation 2027",
    headline: "Reserve your allocation of SPIRUVA phycocyanin & spirulina.",
    sub: "Worldwide delivery begins July 2027. We are accepting allocation reservations now — capacity is limited and we confirm offtake partners by region. Pre-orders are non-binding letters of intent until a Supply Agreement is signed.",
    primary_cta: "Reserve allocation",
    secondary_cta: "Download technical PDFs",
    journal_cta: "Read the latest from the Journal",
    bullets: [
      { icon: Calendar, title: "Shipping window", body: "First commercial shipments leave India in July 2027. Slot priority is set by reservation date and confirmed annual volume." },
      { icon: Globe,    title: "Regional coverage", body: "Active partner conversations across EU, Japan, US, MENA, and ANZ. Distributor exclusivity available per market." },
      { icon: ShieldCheck, title: "Quality & compliance", body: "FSSAI · ISO 22000 · HACCP, with EU Novel Food / Japan-spec phycocyanin tracks already underway." },
      { icon: FileText, title: "Sampling & docs", body: "TDS, CoA templates, allergen and heavy-metal panels available on request after pre-order acknowledgement." },
    ],
    note: "This is a project-stage company. We send a written confirmation within 2 business days of every pre-order request.",
  },
  de: {
    eyebrow: "Vorbestellungen geöffnet · Allokation 2027",
    headline: "Sichern Sie Ihre Allokation von SPIRUVA Phycocyanin & Spirulina.",
    sub: "Weltweite Auslieferung ab Juli 2027. Wir nehmen ab sofort Allokationsreservierungen an — die Kapazität ist begrenzt, und Abnahmepartner werden je Region bestätigt. Vorbestellungen sind unverbindliche Absichtserklärungen bis zur Unterzeichnung eines Liefervertrages.",
    primary_cta: "Allokation reservieren",
    secondary_cta: "Technische PDFs herunterladen",
    journal_cta: "Aktuelle Beiträge im Journal lesen",
    bullets: [
      { icon: Calendar, title: "Lieferzeitraum", body: "Erste kommerzielle Lieferungen verlassen Indien im Juli 2027. Die Slot-Priorität richtet sich nach Reservierungsdatum und bestätigtem Jahresvolumen." },
      { icon: Globe,    title: "Regionale Abdeckung", body: "Aktive Partnergespräche in der EU, Japan, USA, MENA und ANZ. Distributorenexklusivität je Markt verfügbar." },
      { icon: ShieldCheck, title: "Qualität & Compliance", body: "FSSAI · ISO 22000 · HACCP, mit laufenden EU-Novel-Food- und Japan-Spec-Phycocyanin-Tracks." },
      { icon: FileText, title: "Muster & Dokumente", body: "TDS, CoA-Vorlagen sowie Allergen- und Schwermetall-Panels nach Vorbestellungsbestätigung verfügbar." },
    ],
    note: "Wir senden innerhalb von 2 Arbeitstagen nach jeder Vorbestellungsanfrage eine schriftliche Bestätigung.",
  },
  ja: {
    eyebrow: "予約受付中 · 2027 アロケーション",
    headline: "SPIRUVA フィコシアニンとスピルリナのアロケーション枠を予約してください。",
    sub: "2027 年 7 月より全世界出荷を開始します。アロケーション枠の予約を現在受付中です。生産枠には限りがあり、地域ごとにオフテイクパートナーを確定しています。供給契約締結まで、予約は法的拘束力のない意向確認となります。",
    primary_cta: "アロケーションを予約する",
    secondary_cta: "技術 PDF をダウンロード",
    journal_cta: "Journal の最新記事を読む",
    bullets: [
      { icon: Calendar, title: "出荷時期", body: "最初の商業出荷は 2027 年 7 月にインドから発送されます。スロットの優先順位は予約日と確定年間数量により決定されます。" },
      { icon: Globe,    title: "対象地域", body: "EU・日本・米国・MENA・ANZ にて活発なパートナー協議中。地域ごとの独占ディストリビューターも対応可能です。" },
      { icon: ShieldCheck, title: "品質とコンプライアンス", body: "FSSAI・ISO 22000・HACCP に加え、EU Novel Food および日本仕様フィコシアニンのトラックも進行中。" },
      { icon: FileText, title: "サンプルと書類", body: "予約確認後に TDS、CoA テンプレート、アレルゲン・重金属パネル等をご提供します。" },
    ],
    note: "予約リクエストには 2 営業日以内に書面にてご確認をお返しします。",
  },
};

const Preorder = () => {
  const { i18n } = useTranslation();
  const lc = (i18n.language || "en").split("-")[0];
  const t = COPY[lc] || COPY.en;
  const prefix = lc === "en" ? "" : `/${lc}`;

  return (
    <div className="bg-brand-cream min-h-screen" data-testid="preorder-page">
      {/* Hero */}
      <section className="relative bg-brand-black text-brand-cream">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #c9a84c 0, transparent 40%), radial-gradient(circle at 80% 60%, #006d77 0, transparent 35%)" }} />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-brand-gold mb-5" data-testid="preorder-eyebrow">{t.eyebrow}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl" data-testid="preorder-headline">
            {t.headline}
          </h1>
          <p className="mt-6 text-base md:text-lg text-brand-cream/75 max-w-2xl leading-relaxed">
            {t.sub}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to={`${prefix}/reserve-allocation`}
              data-testid="preorder-primary-cta"
              className="inline-flex items-center gap-2 bg-brand-gold text-brand-black px-6 py-3 text-xs font-mono uppercase tracking-[0.22em] hover:bg-brand-gold/90 transition"
            >
              {t.primary_cta} <ArrowRight size={14} />
            </Link>
            <Link
              to={`${prefix}/resources`}
              data-testid="preorder-secondary-cta"
              className="inline-flex items-center gap-2 border border-brand-cream/30 text-brand-cream px-6 py-3 text-xs font-mono uppercase tracking-[0.22em] hover:bg-brand-cream/10 transition"
            >
              {t.secondary_cta}
            </Link>
          </div>
        </div>
      </section>

      {/* Bullets */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid sm:grid-cols-2 gap-5 md:gap-7">
          {t.bullets.map(({ icon: Icon, title, body }, i) => (
            <div key={i} className="p-6 md:p-7 bg-white border border-brand-green/15" data-testid={`preorder-bullet-${i}`}>
              <Icon className="text-brand-teal mb-4" size={22} strokeWidth={1.5} />
              <h3 className="font-display text-xl text-brand-black leading-tight mb-2">{title}</h3>
              <p className="text-sm text-brand-black/70 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
        <p className="mt-12 text-[11px] font-mono uppercase tracking-[0.18em] text-brand-black/55 max-w-2xl">
          {t.note}
        </p>
        <div className="mt-12 pt-8 border-t border-brand-green/15 flex flex-wrap gap-4">
          <Link
            to={`${prefix}/journal`}
            data-testid="preorder-journal-cta"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-brand-teal hover:text-brand-teal/80"
          >
            {t.journal_cta} <ArrowRight size={12} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Preorder;
